import type { JuniorPlusMissionFullState } from '@mission.io/mission-toolkit'
import { CREATIVE_CANVAS_STATION } from '@mission.io/mission-toolkit/constants'
import { getSvgAsString, Tldraw, type Editor } from '@tldraw/tldraw'
import {
	getScreen,
	useJuniorPlusStateSelector,
	useMissionStateSelector,
	useMissionsApiRequest,
	useSendWebsocketMessage,
} from '../../../../context'
import { styled } from 'styled-components'
import { CreativeCanvasDocument } from './CreativeCanvas'
import React, { useCallback, useState } from 'react'

/**
 * Component that wraps the CreativeCanvasDocument and makes sure that the canvas image is uploaded to
 * missions-api.
 *
 * @param {CreativeCanvasDocumentProps} props these props are passed along to the CreativeCanvasDocument
 * @returns {React$Node} A Creative Canvas Document with the exporting prop set to upload the image to missions-api.
 */
const CanvasSingleDocumentUpload = function (props: {
	canvasId: string,
	documentId: string,
}): React$Node {
	const { canvasId, documentId } = props

	const [status, setStatus] = useState()
	React.useEffect(() => {
		setStatus('ready')
	}, [documentId])

	const sendWebsocketMessage = useSendWebsocketMessage()
	const sendMissionsApiRequest = useMissionsApiRequest()

	/**
	 * Callback invoked when CreativeCanvasDocument exports an image
	 * representation of the canvas.
	 * 1. Sends the image to missions api as an svg file,
	 * 2. Based on missions-api response then notifies mission-server to update the document image upload status.
	 */
	const doExport = useCallback(
		async function (editor, ids) {
			const imageBlob = await getExportedSvgBlob(editor, [...ids])
			setStatus('pending')
			const formData = new FormData()
			formData.append('image', imageBlob)
			sendMissionsApiRequest(`/creative-canvas-image/${documentId}`, {
				method: 'POST',
				body: formData,
				headers: { 'Content-Type': `image/svg+xml` },
				onSuccess: () => {
					sendWebsocketMessage({
						type: 'CANVAS_IMAGE_SAVE',
						payload: {
							canvasId,
							documentId,
							status: CREATIVE_CANVAS_STATION.IMAGE_UPLOAD_STATUS.COMPLETE,
						},
					})
				},
				onError: e => {
					console.error('Error while exporting image: ', e)
					sendWebsocketMessage({
						type: 'CANVAS_IMAGE_SAVE',
						payload: {
							canvasId,
							documentId,
							status: CREATIVE_CANVAS_STATION.IMAGE_UPLOAD_STATUS.ERROR,
						},
					})
				},
			}).then(() => {
				setStatus('complete')
			})
		},
		[canvasId, documentId, sendMissionsApiRequest, sendWebsocketMessage]
	)

	return (
		status &&
		status !== 'complete' && (
			<CreativeCanvasDocument
				{...props}
				exporting={{
					documentId,
					doExport: doExport,
					status: status,
				}}
			/>
		)
	)
}

/**
 * Renders all documents that need an upload!
 * @returns
 */
export default function CanvasImageUploader(): React$Node {
	const documents = useDocumentsThatNeedUpload()
	const cannotUnmountTldraw =
		useMissionStateSelector(getScreen)?.type ===
		'COLLABORATIVE_CULMINATING_MOMENT_SCREEN'
	// Render one at a time for performance. when it finishes uploading, its status will change to COMPLETE, and will be removed from this array.
	const firstUp = documents[0]
	return (
		<InvisibleWrapper>
			{firstUp ? (
				<CanvasSingleDocumentUpload
					canvasId={firstUp.canvasId}
					documentId={firstUp.documentId}
				/>
			) : cannotUnmountTldraw ? (
				/* There is a fun little TLDraw side effect that removes some global tl draw variables when a TLDraw canvas is unmounted (those variables are not
			recreated until a new canvas is mounted). 
		We do not want to inadvertently remove any styles from a visible canvas rendering ON SCREEN, so we render an invisible TLDraw canvas here
		until the current screen is no longer a collaborative culminating moment. If we ever change our code and need tldraw to render on a different 
		mission screen, we should reevaluate the logic that sets cannotUnmountTldraw.
		*/
				<EmptyTldraw />
			) : null}
		</InvisibleWrapper>
	)
}

/**
 * A custom hook that determines if a specific document id needs to be uploaded to missions api.
 * @param {string} canvasId
 * @param {string} documentId
 * @returns {boolean}
 */
function useDocumentsThatNeedUpload(): Array<{
	canvasId: string,
	documentId: string,
}> {
	return useJuniorPlusStateSelector((state: ?JuniorPlusMissionFullState) => {
		const canvases = state?.stationData.creativeCanvas.canvases
		const documents: Array<{ canvasId: string, documentId: string }> = []
		if (canvases) {
			Object.keys(canvases).forEach(canvasId => {
				const teams = canvases[canvasId].teams
				documents.push(
					...teams
						.filter(
							team =>
								team.document.imageUploadStatus ===
								CREATIVE_CANVAS_STATION.IMAGE_UPLOAD_STATUS.READY
						)
						.map(team => ({
							canvasId,
							documentId: team.document.id,
						}))
				)
			})
		}
		return documents
	})
}

// An empty tldraw component that keeps styles from unmounting on the visible tldraw canvas.
function EmptyTldraw() {
	return <Tldraw hideUi />
}

const InvisibleWrapper = styled.div`
	position: fixed;
	z-index: 3000;
	height: 30vh;
	width: 40vh;
	top: -30vh;
	left: -40vh;
`
/**
 * Get an svg element that represents the current state of the ids provided on the canvas.
 * @param {Editor} editor
 * @param {string[]} ids
 * @returns
 */
async function getExportSvgElement(editor: Editor, ids: string[]) {
	const svg = await editor.getSvg(ids, {
		scale: 1,
		background: editor.instanceState.exportBackground,
	})
	if (!svg) throw new Error('Could not construct SVG.')
	return svg
}

/**
 * Gets a blob representing an svg image of the ids selected on the canvas.
 * @param {Editor} editor
 * @param {string[]} ids
 * @returns
 */
async function getExportedSvgBlob(editor: Editor, ids: string[]) {
	return new Blob([getSvgAsString(await getExportSvgElement(editor, ids))], {
		type: 'image/svg+xml',
	})
}
